/**
 * Shared signup pages (Foodbuy, HSSI) — form behaviour
 *
 * - Optional section gate: a container with data-show-when-select="<select id>"
 *   and data-show-when-value="<value>" (HSSI Preferred Payment Method, gated on
 *   Management Type = "Marriott Franchised") is shown/enabled only while that
 *   select has the given value; otherwise it is hidden/disabled, its payment
 *   radios are unchecked and the UCA upload is cleared.
 * - Preferred Payment Method: "Payment Terms" reveals/enables the
 *   #signup-payment-terms panel (UCA upload, plus the nested Account Payable
 *   contact on Foodbuy); "Credit Card" hides/disables it and clears the file.
 * - UCA PDF client-side validation (type + size) with dropzone states.
 * - Account Payable "same as account contact" auto-fill.
 * - "Different shipping address" conditional reveal, plus mirroring of
 *   Address Information into the hidden shipping mirror group right before
 *   submit when the checkbox is left unchecked.
 *
 * Disabled fields are excluded from the payload by collectFormDataAsJSON()
 * and attachFilesToPayload() in company-register-v2.js.
 * Rendered markup: snippets/signup-payment-method.liquid,
 * snippets/signup-ap-contact.liquid, snippets/signup-shipping-address.liquid.
 */

"use strict";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIGNUP_PAYMENT_TERMS = "Payment Terms";

const SIGNUP_UCA_INVALID_TYPE_MESSAGE = "Please upload a PDF file.";
const SIGNUP_UCA_TOO_LARGE_MESSAGE = "File is too large. Maximum size is 20 MB.";
const SIGNUP_UCA_PDF_MIME = "application/pdf";
const SIGNUP_UCA_PDF_EXTENSION_PATTERN = /\.pdf$/i;
const SIGNUP_BYTES_PER_MB = 1024 * 1024;
const SIGNUP_DROPZONE_SELECTED_CLASS = "is-selected";
const SIGNUP_DROPZONE_INVALID_CLASS = "is-invalid";
const SIGNUP_DROPZONE_DRAGOVER_CLASS = "is-dragover";
const SIGNUP_AP_COLLAPSED_CLASS = "is-collapsed";

const SIGNUP_IDS = {
  PAYMENT_TERMS: "signup-payment-terms",
  UCA_UPLOAD: "signup-uca-upload",
  UCA_DROPZONE: "signup-uca-dropzone",
  UCA_FILENAME: "signup-uca-filename",
  UCA_ERROR: "signup-uca-error",
  AP_SAME_AS_CONTACT: "signup-ap-same-as-contact",
  AP_FIELDS: "signup-ap-fields",
  AP_NAME: "ap_name",
  AP_EMAIL: "apemail",
  AP_PHONE: "ap_phone",
  FIRST_NAME: "RegisterForm-FirstName",
  LAST_NAME: "RegisterForm-LastName",
  EMAIL: "RegisterForm-email",
  SHIPPING_DIFFERENT: "signup-shipping-different",
  SHIPPING_ADDRESS: "signup-shipping-address",
  SHIPPING_SAME: "signup-shipping-same",
  REGISTRATION_FORM: "registration-form",
};

const SIGNUP_GATE_SELECTOR = "[data-show-when-select]";
const SIGNUP_PAYMENT_RADIO_SELECTOR = 'input[name="customer[payment_method]"]';
const SIGNUP_PHONE_SELECTOR = '#customer_notes[name="customer[phone_number]"]';
const SIGNUP_FIELD_SELECTOR = "input, select, textarea";

/** Billing field id -> hidden shipping mirror field id. */
const SIGNUP_SHIPPING_MIRROR_MAP = [
  ["street_1", "shipping_same_street_1"],
  ["customer_notes_City", "shipping_same_city"],
  ["customer_notes_Province", "shipping_same_state"],
  ["customer_notes_ProvinceCode", "shipping_same_state_code"],
  ["customer_notes_Zip", "shipping_same_zip"],
  ["customer_country", "shipping_same_country"],
  ["customer_country_code", "shipping_same_country_code"],
];

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Show/hide a conditional container and enable/disable every field inside
 * it, so hidden fields are excluded from the submitted payload and from
 * required-field validation.
 *
 * @param {HTMLElement|null} container
 * @param {boolean} enabled
 */
function signupSetSectionEnabled(container, enabled) {
  if (null === container) return;

  container.hidden = !enabled;
  container.querySelectorAll(SIGNUP_FIELD_SELECTOR).forEach((field) => {
    field.disabled = !enabled;
  });
}

/**
 * Format a byte count as a human-readable megabyte string.
 *
 * @param {number} bytes
 * @returns {string}
 */
function signupFormatFileSize(bytes) {
  return (bytes / SIGNUP_BYTES_PER_MB).toFixed(1) + " MB";
}

// ---------------------------------------------------------------------------
// UCA upload
// ---------------------------------------------------------------------------

/**
 * Build the UCA upload controller (reset + validation + drag states).
 *
 * @returns {{clear: function(): void}}
 */
function signupInitUcaUpload() {
  const upload = document.getElementById(SIGNUP_IDS.UCA_UPLOAD);
  const dropzone = document.getElementById(SIGNUP_IDS.UCA_DROPZONE);
  const filename = document.getElementById(SIGNUP_IDS.UCA_FILENAME);
  const errorLine = document.getElementById(SIGNUP_IDS.UCA_ERROR);

  /** Reset the dropzone to its default, empty state. */
  function resetDropzone() {
    if (null !== filename) filename.textContent = filename.dataset.defaultText;
    dropzone?.classList.remove(
      SIGNUP_DROPZONE_SELECTED_CLASS,
      SIGNUP_DROPZONE_INVALID_CLASS,
      SIGNUP_DROPZONE_DRAGOVER_CLASS
    );
  }

  /**
   * Show (non-empty message) or hide (empty message) the UCA error line.
   *
   * @param {string} message
   */
  function setError(message) {
    upload?.setCustomValidity(message);
    if (null === errorLine) return;
    errorLine.textContent = message;
    errorLine.hidden = !message;
  }

  /** Clear the selected file, validity, error line and dropzone state. */
  function clear() {
    if (null !== upload) upload.value = "";
    setError("");
    resetDropzone();
  }

  /**
   * Validate a selected file and return the error message ("" when valid).
   *
   * @param {File} file
   * @returns {string}
   */
  function validateFile(file) {
    const isPdf = file.type === SIGNUP_UCA_PDF_MIME || SIGNUP_UCA_PDF_EXTENSION_PATTERN.test(file.name);
    const maxBytes = Number(upload.dataset.maxBytes);
    let message = "";
    if (!isPdf) {
      message = SIGNUP_UCA_INVALID_TYPE_MESSAGE;
    } else if (maxBytes && file.size > maxBytes) {
      message = SIGNUP_UCA_TOO_LARGE_MESSAGE;
    }
    return message;
  }

  /** Handle a file selection on the UCA input. */
  function handleChange() {
    resetDropzone();
    setError("");

    const file = upload.files?.[0];
    if (!file) return;

    const message = validateFile(file);
    if (message) {
      setError(message);
      dropzone?.classList.add(SIGNUP_DROPZONE_INVALID_CLASS);
      if (null !== filename) filename.textContent = file.name;
    } else {
      dropzone?.classList.add(SIGNUP_DROPZONE_SELECTED_CLASS);
      if (null !== filename) filename.textContent = `${file.name} (${signupFormatFileSize(file.size)})`;
    }
    upload.reportValidity();
  }

  if (null !== upload) {
    upload.addEventListener("change", handleChange);
  }

  if (null !== dropzone) {
    ["dragenter", "dragover"].forEach((eventName) => {
      dropzone.addEventListener(eventName, () => dropzone.classList.add(SIGNUP_DROPZONE_DRAGOVER_CLASS));
    });
    ["dragleave", "drop"].forEach((eventName) => {
      dropzone.addEventListener(eventName, () => dropzone.classList.remove(SIGNUP_DROPZONE_DRAGOVER_CLASS));
    });
  }

  return { clear };
}

// ---------------------------------------------------------------------------
// Section gate -> Preferred Payment Method -> Payment Terms panel
// ---------------------------------------------------------------------------

/**
 * Wire the optional section gate(s) and the Preferred Payment Method radios.
 *
 * @param {{clear: function(): void}} ucaUpload
 */
function signupInitPaymentToggles(ucaUpload) {
  const paymentTermsPanel = document.getElementById(SIGNUP_IDS.PAYMENT_TERMS);
  const paymentRadios = document.querySelectorAll(SIGNUP_PAYMENT_RADIO_SELECTOR);

  /**
   * Enable the Payment Terms panel only when "Payment Terms" is the checked
   * (and enabled) radio; otherwise disable it and clear any selected file.
   */
  function syncPaymentTermsPanel() {
    const termsRadio = Array.from(paymentRadios).find((radio) => radio.value === SIGNUP_PAYMENT_TERMS);
    const isPaymentTerms = Boolean(termsRadio?.checked) && !termsRadio.disabled;
    signupSetSectionEnabled(paymentTermsPanel, isPaymentTerms);
    if (!isPaymentTerms) ucaUpload.clear();
  }

  document.querySelectorAll(SIGNUP_GATE_SELECTOR).forEach((container) => {
    const gateSelect = document.getElementById(container.dataset.showWhenSelect);
    if (null === gateSelect) return;

    gateSelect.addEventListener("change", () => {
      const isOpen = gateSelect.value === container.dataset.showWhenValue;
      signupSetSectionEnabled(container, isOpen);
      if (!isOpen) {
        container.querySelectorAll(SIGNUP_PAYMENT_RADIO_SELECTOR).forEach((radio) => {
          radio.checked = false;
        });
      }
      syncPaymentTermsPanel();
    });
  });

  paymentRadios.forEach((radio) => {
    radio.addEventListener("change", syncPaymentTermsPanel);
  });
}

// ---------------------------------------------------------------------------
// Account Payable "same as account contact"
// ---------------------------------------------------------------------------

/** Wire the AP "same as account contact above" checkbox. */
function signupInitApSameAsContact() {
  const checkbox = document.getElementById(SIGNUP_IDS.AP_SAME_AS_CONTACT);
  if (null === checkbox) return;

  const apFields = document.getElementById(SIGNUP_IDS.AP_FIELDS);
  const apName = document.getElementById(SIGNUP_IDS.AP_NAME);
  const apEmail = document.getElementById(SIGNUP_IDS.AP_EMAIL);
  const apPhone = document.getElementById(SIGNUP_IDS.AP_PHONE);
  const firstName = document.getElementById(SIGNUP_IDS.FIRST_NAME);
  const lastName = document.getElementById(SIGNUP_IDS.LAST_NAME);
  const email = document.getElementById(SIGNUP_IDS.EMAIL);
  const phone = document.querySelector(SIGNUP_PHONE_SELECTOR);

  /** Copy account contact values into the AP fields while checked. */
  function syncFromAccountContact() {
    if (!checkbox.checked) return;
    if (null !== apName) apName.value = [firstName?.value, lastName?.value].filter(Boolean).join(" ");
    if (null !== apEmail) apEmail.value = (email?.value ?? "").trim().toLowerCase();
    if (null !== apPhone) apPhone.value = phone?.value ?? "";
  }

  checkbox.addEventListener("change", () => {
    if (checkbox.checked) {
      syncFromAccountContact();
      apFields?.classList.add(SIGNUP_AP_COLLAPSED_CLASS);
    } else {
      [apName, apEmail, apPhone].forEach((field) => {
        if (null !== field) field.value = "";
      });
      apFields?.classList.remove(SIGNUP_AP_COLLAPSED_CLASS);
    }
  });

  [firstName, lastName, email, phone].forEach((field) => {
    field?.addEventListener("input", syncFromAccountContact);
  });
}

// ---------------------------------------------------------------------------
// Different shipping address
// ---------------------------------------------------------------------------

/** Wire the "different shipping address" toggle and billing mirror. */
function signupInitShipping() {
  const checkbox = document.getElementById(SIGNUP_IDS.SHIPPING_DIFFERENT);
  const shippingAddress = document.getElementById(SIGNUP_IDS.SHIPPING_ADDRESS);
  const mirrorGroup = document.getElementById(SIGNUP_IDS.SHIPPING_SAME);

  /**
   * Disable the hidden mirror group while a different shipping address is
   * entered (visible fields are used), enable it otherwise.
   *
   * @param {boolean} shippingDifferent
   */
  function setMirrorEnabled(shippingDifferent) {
    mirrorGroup?.querySelectorAll("input").forEach((field) => {
      field.disabled = shippingDifferent;
    });
  }

  /** Copy Address Information into the mirror fields when unchecked. */
  function syncShippingFromBilling() {
    if (checkbox?.checked) return;
    SIGNUP_SHIPPING_MIRROR_MAP.forEach(([sourceId, targetId]) => {
      const source = document.getElementById(sourceId);
      const target = document.getElementById(targetId);
      if (null !== source && null !== target) target.value = source.value;
    });
  }

  setMirrorEnabled(Boolean(checkbox?.checked));
  checkbox?.addEventListener("change", () => {
    signupSetSectionEnabled(shippingAddress, checkbox.checked);
    setMirrorEnabled(checkbox.checked);
  });

  // Capture phase so the mirror is synced before company-register-v2.js's
  // own (bubble-phase, form-bound) submit listener builds the payload.
  document.addEventListener(
    "submit",
    (event) => {
      if (event.target?.id === SIGNUP_IDS.REGISTRATION_FORM) syncShippingFromBilling();
    },
    true
  );
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

document.addEventListener("DOMContentLoaded", () => {
  const ucaUpload = signupInitUcaUpload();
  signupInitPaymentToggles(ucaUpload);
  signupInitApSameAsContact();
  signupInitShipping();
});
